import { absoluteValue } from '@0xtorch/big-decimal'
import type { Action } from '@0xtorch/core'
import {
  ApiSecretRequiredException,
  AssetNotFoundException,
} from '../../errors'
import type { FetchApiData } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import { backpackAccountCategory } from '../constants'
import { createClient } from '../createClient'
import { type FundingPayment, fundingPaymentsResponseSchema } from './schema'

/** 1 ページあたり API が返す最大件数 (Backpack 仕様の上限) */
const PAGE_LIMIT = 1000

/**
 * 安全装置として 1 回の `fetchApiData` 呼び出しで取得する最大ページ数。
 * 1000 件 × 1000 ページ = 100 万件まで耐える設計とする。
 */
const MAX_PAGES = 1000

/**
 * naive ISO date-time を UTC として解釈し UNIX milliseconds に変換する。
 */
const parseIntervalEnd = (value: string): number => {
  const hasTimezone = /([Zz]|[+-]\d{2}:?\d{2})$/.test(value)
  const isoString = hasTimezone ? value : `${value}Z`
  const ms = Date.parse(isoString)
  if (Number.isNaN(ms)) {
    throw new Error(`Invalid Backpack intervalEndTimestamp: ${value}`)
  }
  return ms
}

/**
 * Backpack perp のファンディング履歴を取得し、各 interval について Action を生成する。
 *
 * 当 endpoint は `from`/`to` フィルタを持たず offset ベースの pagination のみ。
 * sortDirection=Desc (新しい順) で取得し、 framework から渡された `from` より古い record に達したら
 * early-stop することで効率化する。
 */
export const fetchApiData: FetchApiData = async ({
  apiKey,
  apiSecret,
  assets,
  config,
  createHeaders,
  from,
  proxy,
  symbolAssetMap,
  to,
}) => {
  if (!apiSecret) {
    throw new ApiSecretRequiredException()
  }

  const getAsset = createGetAsset({ assets, symbolAssetMap })

  const client = createClient({
    apiKey,
    apiSecret,
    createHeaders,
    proxy,
  })

  const collected: (FundingPayment & { timestampMs: number })[] = []
  let earlyStop = false

  for (let page = 0; page < MAX_PAGES; page++) {
    const response = await client.run({
      method: 'GET',
      path: '/wapi/v1/history/funding',
      query: {
        limit: PAGE_LIMIT.toString(),
        offset: (page * PAGE_LIMIT).toString(),
        sortDirection: 'Desc',
      },
      responseSchema: fundingPaymentsResponseSchema,
    })

    for (const item of response) {
      const timestampMs = parseIntervalEnd(item.intervalEndTimestamp)
      if (from !== undefined && timestampMs < from) {
        earlyStop = true
        break
      }
      if (to !== undefined && timestampMs > to) {
        continue
      }
      collected.push({ ...item, timestampMs })
    }

    if (earlyStop || response.length < PAGE_LIMIT) {
      break
    }
  }

  collected.sort((a, b) => a.timestampMs - b.timestampMs)

  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const sources: string[] = []
  const actions: Action[] = []

  for (const item of collected) {
    fetchedFrom =
      fetchedFrom === undefined
        ? item.timestampMs
        : Math.min(fetchedFrom, item.timestampMs)
    fetchedTo =
      fetchedTo === undefined
        ? item.timestampMs
        : Math.max(fetchedTo, item.timestampMs)

    if (item.quantity.value === 0n) {
      continue
    }

    // SOL_USDC_PERP -> quote = USDC
    const parts = item.symbol.split('_')
    if (parts.length < 2) {
      throw new Error(`Invalid Backpack perp symbol: ${item.symbol}`)
    }
    const quote = parts[1]
    const quoteAsset = getAsset(quote)
    if (!quoteAsset) {
      throw new AssetNotFoundException(quote)
    }

    actions.push({
      comment: `Funding of ${item.symbol}`,
      evidence: 'system-rule',
      order: 0,
      source: `${backpackAccountCategory}_${config.type}_${item.symbol}_${item.timestampMs}`,
      type: item.quantity.value > 0n ? 'derivative-profit' : 'derivative-fee',
      timestamp: item.timestampMs,
      transfers: [
        {
          asset: quoteAsset,
          amount: absoluteValue(item.quantity),
          direction: item.quantity.value > 0n ? 'in' : 'out',
        },
      ],
    })
  }

  const sortedActions = actions.sort((a, b) => a.timestamp - b.timestamp)

  return {
    actions: sortedActions,
    count: collected.length,
    from: fetchedFrom,
    sources,
    stopped: false,
    to: fetchedTo,
  }
}
